import React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  HiOutlineDocumentText,
  HiOutlineUser,
  HiOutlineOfficeBuilding,
  HiOutlineCalendar,
  HiOutlinePencilAlt,
  HiOutlineChatAlt2,
  HiOutlineClipboardList,
  HiOutlineLightBulb,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import { Copy } from "../../atoms/Copy";
import { Placeholder, VarList, VarRow } from "../../common/VarList";
import CollapsibleHowItWorks from "../../common/CollapsibleHowItWorks";

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ icon, title, defaultOpen = false, children }) => (
  <CollapsibleHowItWorks
    collapsible
    title={title}
    icon={icon}
    defaultOpen={defaultOpen}
    size="lg"
  >
    {children}
  </CollapsibleHowItWorks>
);

const Step: React.FC<{
  number: number;
  title: string;
  children: React.ReactNode;
}> = ({ number, title, children }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-semibold">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="text-sm font-semibold text-gray-800 mb-1">{title}</h4>
      <div className="text-sm text-gray-600 leading-relaxed">{children}</div>
    </div>
  </div>
);

const CopyableTag: React.FC<{
  placeholder: string;
  description: string;
  copyTitle: string;
}> = ({ placeholder, description, copyTitle }) => {
  return (
    <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
      <div className="flex-1 min-w-0">
        <span className="text-xs text-gray-500 block">{description}</span>
        <code
          translate="no"
          className="text-xs font-mono text-blue-700 break-all"
        >
          {placeholder}
        </code>
      </div>
      <Copy
        target={placeholder}
        iconOnly
        title={copyTitle}
        className="flex-shrink-0 ml-2 p-1.5 rounded hover:bg-gray-200 transition-colors"
        iconClassName="h-3.5 w-3.5"
      />
    </div>
  );
};

const ESignatureHelp: React.FC = () => {
  const { t } = useTranslation();
  const copyTitle = t("esignature.help.copyTitle") as string;

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold text-blue-800 flex items-center gap-2">
          <HiOutlineLightBulb size={22} />
          {t("esignature.help.intro.title")}
        </h2>
        <p className="mt-2 text-sm text-blue-700 leading-relaxed">
          <Trans
            t={t}
            i18nKey="esignature.help.intro.body"
            components={{
              ph: <Placeholder>{"{{user_first_name}}"}</Placeholder>,
            }}
          />
        </p>
      </div>

      <Section
        icon={<HiOutlineClipboardList size={20} className="text-gray-400" />}
        title={t("esignature.help.quickRef.title") as string}
        defaultOpen={true}
      >
        <p className="text-sm text-gray-600 mb-4">
          {t("esignature.help.quickRef.description")}
        </p>

        <div className="mb-5">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <HiOutlineUser size={14} className="text-gray-400" />
            {t("esignature.help.quickRef.signerInfoTitle")}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <CopyableTag
              placeholder="{{user_first_name}}"
              description={t("esignature.help.fields.firstName") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{user_last_name}}"
              description={t("esignature.help.fields.lastName") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{user_full_name}}"
              description={t("esignature.help.fields.fullName") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{user_email}}"
              description={t("esignature.help.fields.email") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{user_phone}}"
              description={t("esignature.help.fields.phone") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{user_identification}}"
              description={t("esignature.help.fields.id") as string}
              copyTitle={copyTitle}
            />
          </div>
        </div>

        <div className="mb-5">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <HiOutlineOfficeBuilding size={14} className="text-gray-400" />
            {t("esignature.help.quickRef.businessInfoTitle")}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <CopyableTag
              placeholder="{{entity_name}}"
              description={t("esignature.help.fields.businessName") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{entity_email}}"
              description={t("esignature.help.fields.businessEmail") as string}
              copyTitle={copyTitle}
            />
          </div>
        </div>

        <div className="mb-5">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <HiOutlineCalendar size={14} className="text-gray-400" />
            {t("esignature.help.quickRef.dateTimeTitle")}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <CopyableTag
              placeholder="{{date}}"
              description={t("esignature.help.fields.todayDate") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{signature_date}}"
              description={t("esignature.help.fields.signingDate") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{signature_time}}"
              description={t("esignature.help.fields.signingTime") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{signature_datetime}}"
              description={
                t("esignature.help.fields.signingDateTime") as string
              }
              copyTitle={copyTitle}
            />
          </div>
        </div>

        <div className="mb-5">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <HiOutlinePencilAlt size={14} className="text-gray-400" />
            {t("esignature.help.quickRef.signatureInitialsTitle")}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <CopyableTag
              placeholder="{{signature}}"
              description={t("esignature.help.fields.signatureDesc") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{initials}}"
              description={t("esignature.help.fields.initialsDesc") as string}
              copyTitle={copyTitle}
            />
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <HiOutlineChatAlt2 size={14} className="text-gray-400" />
            {t("esignature.help.quickRef.customInputTitle")}
          </h4>
          <p className="text-xs text-gray-500 mb-2">
            {t("esignature.help.quickRef.customInputHint")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <CopyableTag
              placeholder="{{custom.}}"
              description={t("esignature.help.fields.customDesc") as string}
              copyTitle={copyTitle}
            />
            <CopyableTag
              placeholder="{{input.}}"
              description={t("esignature.help.fields.inputDesc") as string}
              copyTitle={copyTitle}
            />
          </div>
        </div>
      </Section>

      <Section
        icon={<HiOutlineClipboardList size={20} className="text-gray-400" />}
        title={t("esignature.help.steps.title") as string}
        defaultOpen={true}
      >
        <div className="space-y-5">
          <Step
            number={1}
            title={t("esignature.help.steps.step1.title") as string}
          >
            <p>{t("esignature.help.steps.step1.body")}</p>
          </Step>

          <Step
            number={2}
            title={t("esignature.help.steps.step2.title") as string}
          >
            <p>
              <Trans
                t={t}
                i18nKey="esignature.help.steps.step2.body"
                components={{
                  ph: <Placeholder>{"{{user_first_name}}"}</Placeholder>,
                }}
              />
            </p>
            <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                {t("esignature.help.steps.step2.exampleLabel")}
              </p>
              <p className="text-sm text-gray-800 leading-relaxed font-serif">
                <Trans
                  t={t}
                  i18nKey="esignature.help.steps.step2.exampleBefore"
                  components={{
                    first: <Placeholder>{"{{user_first_name}}"}</Placeholder>,
                    last: <Placeholder>{"{{user_last_name}}"}</Placeholder>,
                    entity: <Placeholder>{"{{entity_name}}"}</Placeholder>,
                  }}
                />
              </p>
              <p className="mt-3 text-xs text-gray-500">
                {t("esignature.help.steps.step2.exampleArrow")}
              </p>
              <p className="mt-1 text-sm text-gray-800 leading-relaxed font-serif">
                <Trans
                  t={t}
                  i18nKey="esignature.help.steps.step2.exampleAfter"
                  components={{
                    first: <span className="font-semibold text-blue-700" />,
                    last: <span className="font-semibold text-blue-700" />,
                    entity: <span className="font-semibold text-blue-700" />,
                  }}
                />
              </p>
            </div>
          </Step>

          <Step
            number={3}
            title={t("esignature.help.steps.step3.title") as string}
          >
            <p>
              <Trans
                t={t}
                i18nKey="esignature.help.steps.step3.body"
                components={{ strong: <strong /> }}
              />
            </p>
          </Step>

          <Step
            number={4}
            title={t("esignature.help.steps.step4.title") as string}
          >
            <p>
              <Trans
                t={t}
                i18nKey="esignature.help.steps.step4.body"
                components={{ strong: <strong /> }}
              />
            </p>
          </Step>

          <Step
            number={5}
            title={t("esignature.help.steps.step5.title") as string}
          >
            <p>
              <Trans
                t={t}
                i18nKey="esignature.help.steps.step5.body"
                components={{ strong: <strong /> }}
              />
            </p>
          </Step>
        </div>
      </Section>

      <Section
        icon={<HiOutlineUser size={20} className="text-gray-400" />}
        title={t("esignature.help.signerTable.title") as string}
      >
        <p className="text-sm text-gray-600 mb-3">
          {t("esignature.help.signerTable.description")}
        </p>
        <VarList>
          <VarRow
            placeholder="{{user_first_name}}"
            description={
              t("esignature.help.signerTable.firstNameDesc") as string
            }
            example="John"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{user_last_name}}"
            description={
              t("esignature.help.signerTable.lastNameDesc") as string
            }
            example="Doe"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{user_full_name}}"
            description={
              t("esignature.help.signerTable.fullNameDesc") as string
            }
            example="John Doe"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{user_email}}"
            description={t("esignature.help.signerTable.emailDesc") as string}
            example="john.doe@example.com"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{user_phone}}"
            description={t("esignature.help.signerTable.phoneDesc") as string}
            example="+1 514 555 1234"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{user_identification}}"
            description={t("esignature.help.signerTable.idDesc") as string}
            example="USR-00042"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
        </VarList>
      </Section>

      <Section
        icon={<HiOutlineOfficeBuilding size={20} className="text-gray-400" />}
        title={t("esignature.help.businessTable.title") as string}
      >
        <p className="text-sm text-gray-600 mb-3">
          {t("esignature.help.businessTable.description")}
        </p>
        <VarList>
          <VarRow
            placeholder="{{entity_name}}"
            description={t("esignature.help.businessTable.nameDesc") as string}
            example="Acme Loyalty Inc."
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{entity_email}}"
            description={t("esignature.help.businessTable.emailDesc") as string}
            example="hello@acme.com"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
        </VarList>
      </Section>

      <Section
        icon={<HiOutlineCalendar size={20} className="text-gray-400" />}
        title={t("esignature.help.dateTimeTable.title") as string}
      >
        <p className="text-sm text-gray-600 mb-3">
          {t("esignature.help.dateTimeTable.description")}
        </p>
        <VarList>
          <VarRow
            placeholder="{{date}}"
            description={t("esignature.help.dateTimeTable.todayDesc") as string}
            example="2026-04-10"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{signature_date}}"
            description={
              t("esignature.help.dateTimeTable.signingDateDesc") as string
            }
            example="2026-04-10"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{signature_time}}"
            description={
              t("esignature.help.dateTimeTable.signingTimeDesc") as string
            }
            example="14:30:05"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
          <VarRow
            placeholder="{{signature_datetime}}"
            description={
              t("esignature.help.dateTimeTable.signingDateTimeDesc") as string
            }
            example="2026-04-10 14:30:05"
            descriptionLabel={t("esignature.help.table.description") as string}
            exampleLabel={t("esignature.help.table.example") as string}
          />
        </VarList>
      </Section>

      <Section
        icon={<HiOutlinePencilAlt size={20} className="text-gray-400" />}
        title={t("esignature.help.signatureTable.title") as string}
      >
        <p className="text-sm text-gray-600 mb-3">
          {t("esignature.help.signatureTable.description")}
        </p>
        <VarList>
          <VarRow
            placeholder="{{signature}}"
            description={
              t("esignature.help.signatureTable.signatureDesc") as string
            }
          />
          <VarRow
            placeholder="{{initials}}"
            description={
              t("esignature.help.signatureTable.initialsDesc") as string
            }
          />
        </VarList>
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            <Trans
              t={t}
              i18nKey="esignature.help.signatureTable.tip"
              components={{
                strong: <strong />,
                ph: <Placeholder>{"{{signature}}"}</Placeholder>,
              }}
            />
          </p>
        </div>
      </Section>

      <Section
        icon={<HiOutlineDocumentText size={20} className="text-gray-400" />}
        title={t("esignature.help.customData.title") as string}
      >
        <p className="text-sm text-gray-600 mb-3">
          <Trans
            t={t}
            i18nKey="esignature.help.customData.description"
            components={{ ph: <Placeholder>{"{{custom.…}}"}</Placeholder> }}
          />
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t("esignature.help.customData.howItWorksLabel")}
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            <Trans
              t={t}
              i18nKey="esignature.help.customData.howItWorksBody"
              components={{
                code: (
                  <code
                    translate="no"
                    className="text-xs bg-gray-200 px-1 rounded"
                  />
                ),
              }}
            />
          </p>
          <div className="mt-2">
            <Placeholder>
              {t("esignature.help.customData.exampleToken") as string}
            </Placeholder>
          </div>
        </div>
        <VarList>
          <VarRow
            placeholder="{{custom.custom_registration_answers.question}}"
            description={t("esignature.help.customData.answerDesc") as string}
          />
        </VarList>
      </Section>

      <Section
        icon={<HiOutlineChatAlt2 size={20} className="text-gray-400" />}
        title={t("esignature.help.inputFields.title") as string}
      >
        <p className="text-sm text-gray-600 mb-3">
          <Trans
            t={t}
            i18nKey="esignature.help.inputFields.description"
            components={{ ph: <Placeholder>{"{{input.…}}"}</Placeholder> }}
          />
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t("esignature.help.inputFields.howItWorksLabel")}
          </p>
          <ol className="text-sm text-gray-700 space-y-2">
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold mt-0.5">
                1
              </span>
              <span>
                <Trans
                  t={t}
                  i18nKey="esignature.help.inputFields.step1"
                  components={{
                    ph: (
                      <Placeholder>{"{{input.emergency contact}}"}</Placeholder>
                    ),
                  }}
                />
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold mt-0.5">
                2
              </span>
              <span>
                <Trans
                  t={t}
                  i18nKey="esignature.help.inputFields.step2"
                  components={{ strong: <strong /> }}
                />
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold mt-0.5">
                3
              </span>
              <span>{t("esignature.help.inputFields.step3")}</span>
            </li>
          </ol>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          <Trans
            t={t}
            i18nKey="esignature.help.inputFields.labelHint"
            components={{
              code: (
                <code
                  translate="no"
                  className="text-xs bg-gray-200 px-1 rounded"
                />
              ),
            }}
          />
        </p>
        <VarList>
          <VarRow
            placeholder={t("esignature.help.inputFields.token1") as string}
            description={t("esignature.help.inputFields.label1") as string}
            descriptionLabel={
              t("esignature.help.inputFields.signerSees") as string
            }
          />
          <VarRow
            placeholder={t("esignature.help.inputFields.token2") as string}
            description={t("esignature.help.inputFields.label2") as string}
            descriptionLabel={
              t("esignature.help.inputFields.signerSees") as string
            }
          />
          <VarRow
            placeholder={t("esignature.help.inputFields.token3") as string}
            description={t("esignature.help.inputFields.label3") as string}
            descriptionLabel={
              t("esignature.help.inputFields.signerSees") as string
            }
          />
          <VarRow
            placeholder={t("esignature.help.inputFields.token4") as string}
            description={t("esignature.help.inputFields.label4") as string}
            descriptionLabel={
              t("esignature.help.inputFields.signerSees") as string
            }
          />
        </VarList>
      </Section>

      <Section
        icon={<HiOutlineCheckCircle size={20} className="text-gray-400" />}
        title={t("esignature.help.choiceFields.title") as string}
      >
        <p className="text-sm text-gray-600 mb-3">
          <Trans
            t={t}
            i18nKey="esignature.help.choiceFields.description"
            components={{
              ph1: <Placeholder>{"{{choice.title.…}}"}</Placeholder>,
              ph2: <Placeholder>{"{{choice.option.…}}"}</Placeholder>,
            }}
          />
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t("esignature.help.choiceFields.howItWorksLabel")}
          </p>
          <ol className="text-sm text-gray-700 space-y-2">
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold mt-0.5">
                1
              </span>
              <span>
                <Trans
                  t={t}
                  i18nKey="esignature.help.choiceFields.step1"
                  components={{
                    ph1: (
                      <Placeholder>
                        {"{{choice.title.What's your favourite pet?}}"}
                      </Placeholder>
                    ),
                    ph2: <Placeholder>{"{{choice.option….}}"}</Placeholder>,
                  }}
                />
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold mt-0.5">
                2
              </span>
              <span>{t("esignature.help.choiceFields.step2")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold mt-0.5">
                3
              </span>
              <span>{t("esignature.help.choiceFields.step3")}</span>
            </li>
          </ol>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          <Trans
            t={t}
            i18nKey="esignature.help.choiceFields.labelHint"
            components={{
              code: (
                <code
                  translate="no"
                  className="text-xs bg-gray-200 px-1 rounded"
                />
              ),
            }}
          />
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t("esignature.help.choiceFields.exampleLabel")}
          </p>
          <div className="space-y-1.5 flex flex-col">
            <Placeholder>
              {t("esignature.help.choiceFields.titleToken") as string}
            </Placeholder>
            <Placeholder>
              {t("esignature.help.choiceFields.optionToken1") as string}
            </Placeholder>
            <Placeholder>
              {t("esignature.help.choiceFields.optionToken2") as string}
            </Placeholder>
          </div>

          <p className="mt-4 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t("esignature.help.choiceFields.signerSees")}
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-800 mb-2">
              {t("esignature.help.choiceFields.titleLabel")}
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-1">
              <input
                type="checkbox"
                disabled
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("esignature.help.choiceFields.optionLabel1")}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                disabled
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("esignature.help.choiceFields.optionLabel2")}
            </label>
          </div>
        </div>
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            {t("esignature.help.choiceFields.optionalNote")}
          </p>
        </div>

        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t("esignature.help.choiceFields.requiredLabel")}
          </p>
          <p className="text-sm text-gray-700 mb-3">
            <Trans
              t={t}
              i18nKey="esignature.help.choiceFields.requiredHint"
              components={{
                code: (
                  <code
                    translate="no"
                    className="text-xs bg-gray-200 px-1 rounded"
                  />
                ),
              }}
            />
          </p>
          <Placeholder>
            {t("esignature.help.choiceFields.requiredToken") as string}
          </Placeholder>
          <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-800">
              {t("esignature.help.choiceFields.titleLabel")}
              <span className="text-red-500 ml-1">*</span>
            </p>
          </div>
        </div>

        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            <Trans
              t={t}
              i18nKey="esignature.help.choiceFields.tableHint"
              components={{ strong: <strong /> }}
            />
          </p>
        </div>
      </Section>

      <Section
        icon={<HiOutlineLightBulb size={20} className="text-gray-400" />}
        title={t("esignature.help.tips.title") as string}
      >
        <div className="space-y-4">
          <TipRow title={t("esignature.help.tips.shortNames.title") as string}>
            <Trans
              t={t}
              i18nKey="esignature.help.tips.shortNames.body"
              components={{
                ph: (
                  <Placeholder>
                    {
                      "{{custom.custom_registration_answers.do you like our service ?}}"
                    }
                  </Placeholder>
                ),
              }}
            />
          </TipRow>

          <TipRow
            title={t("esignature.help.tips.doubleBraces.title") as string}
          >
            <Trans
              t={t}
              i18nKey="esignature.help.tips.doubleBraces.body"
              components={{
                openCode: (
                  <code
                    translate="no"
                    className="text-xs bg-gray-200 px-1 rounded"
                  />
                ),
                closeCode: (
                  <code
                    translate="no"
                    className="text-xs bg-gray-200 px-1 rounded"
                  />
                ),
                badCode: (
                  <code
                    translate="no"
                    className="text-xs bg-gray-200 px-1 rounded"
                  />
                ),
              }}
            />
          </TipRow>

          <TipRow
            title={t("esignature.help.tips.exactSpelling.title") as string}
          >
            <Trans
              t={t}
              i18nKey="esignature.help.tips.exactSpelling.body"
              components={{
                good: <Placeholder>{"{{user_first_name}}"}</Placeholder>,
                bad: <Placeholder>{"{{User First Name}}"}</Placeholder>,
              }}
            />
          </TipRow>

          <TipRow
            title={t("esignature.help.tips.noBraceInside.title") as string}
          >
            <Trans
              t={t}
              i18nKey="esignature.help.tips.noBraceInside.body"
              components={{
                code: (
                  <code
                    translate="no"
                    className="text-xs bg-gray-200 px-1 rounded"
                  />
                ),
              }}
            />
          </TipRow>

          <TipRow
            title={t("esignature.help.tips.signaturePlacement.title") as string}
          >
            {t("esignature.help.tips.signaturePlacement.body")}
          </TipRow>

          <TipRow
            title={t("esignature.help.tips.borderMargin.title") as string}
          >
            {t("esignature.help.tips.borderMargin.body")}
          </TipRow>
          <div className="ml-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <figure className="m-0">
              <img
                src="https://cdn.1cdn.app/application/UMD/26042309571189_dont.png"
                alt={t("esignature.help.tips.borderMargin.dontAlt") as string}
                className="rounded-lg border border-gray-200 w-full"
              />
              <figcaption className="mt-2 text-xs font-semibold text-red-600 uppercase tracking-wide">
                {t("esignature.help.tips.borderMargin.dontLabel")}
              </figcaption>
            </figure>
            <figure className="m-0">
              <img
                src="https://cdn.1cdn.app/application/UMD/2604230957118_do.png"
                alt={t("esignature.help.tips.borderMargin.doAlt") as string}
                className="rounded-lg border border-gray-200 w-full"
              />
              <figcaption className="mt-2 text-xs font-semibold text-green-600 uppercase tracking-wide">
                {t("esignature.help.tips.borderMargin.doLabel")}
              </figcaption>
            </figure>
          </div>

          <TipRow title={t("esignature.help.tips.preview.title") as string}>
            {t("esignature.help.tips.preview.body")}
          </TipRow>

          <TipRow title={t("esignature.help.tips.pdfExport.title") as string}>
            {t("esignature.help.tips.pdfExport.body")}
          </TipRow>

          <TipRow title={t("esignature.help.tips.inputLimit.title") as string}>
            <Trans
              t={t}
              i18nKey="esignature.help.tips.inputLimit.body"
              components={{
                ph: <Placeholder>{"{{input.…}}"}</Placeholder>,
                line1: <Placeholder>{"{{input.address line 1}}"}</Placeholder>,
                line2: <Placeholder>{"{{input.address line 2}}"}</Placeholder>,
              }}
            />
          </TipRow>

          <TipRow title={t("esignature.help.tips.leaveSpace.title") as string}>
            {t("esignature.help.tips.leaveSpace.body")}
          </TipRow>
        </div>
      </Section>
    </div>
  );
};

const TipRow: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="flex items-start gap-3">
    <span className="flex-shrink-0 mt-0.5 text-gray-400">
      <HiOutlineCheckCircle size={18} />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-800">{title}</p>
      <p className="text-sm text-gray-600 mt-0.5">{children}</p>
    </div>
  </div>
);

export default ESignatureHelp;
